import React, { useEffect, useState } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Container from 'components/Container';
import UserDashboard from 'layouts/UserDashboard';
import { getEntitlement } from './FinancialService';
import { EntitlementInfo } from './types';
import { FeatureKeys } from './FeatureKeys';

const C = {
  ink: '#17263d',
  muted: '#78869a',
  blue: '#1475d1',
  green: '#159b72',
  red: '#ff454d',
  orange: '#ff8b32',
};

const FEATURE_CATALOGUE = [
  {
    key: FeatureKeys.MONEY_SNAPSHOT,
    label: 'Spending snapshot',
    description: 'Budget dial, totals and net position',
    minTier: 'Free',
  },
  {
    key: FeatureKeys.MONEY_RATIO,
    label: 'Ratio breakdown',
    description: '50/30/20 Needs / Wants / Savings view',
    minTier: 'Free',
  },
  {
    key: FeatureKeys.MONEY_TRANSACTIONS,
    label: 'Transaction history',
    description: 'Full transaction log with categories',
    minTier: 'Free',
  },
  {
    key: FeatureKeys.MONEY_COACH,
    label: 'Money Coach',
    description: 'Personalised insights and action plans',
    minTier: 'Plus',
  },
  {
    key: FeatureKeys.MONEY_FORECAST,
    label: 'Spending forecast',
    description: 'Month-end projection and trajectory',
    minTier: 'Plus',
  },
  {
    key: FeatureKeys.MONEY_CSV_IMPORT,
    label: 'CSV import',
    description: 'Import transactions from any bank export',
    minTier: 'Plus',
  },
  {
    key: FeatureKeys.GOALS_CORE,
    label: 'Goals',
    description: 'Create and track unlimited savings goals',
    minTier: 'Plus',
  },
  {
    key: FeatureKeys.HOUSEHOLD_CORE,
    label: 'Household',
    description: 'Shared finances with your partner',
    minTier: 'Family',
  },
  {
    key: FeatureKeys.BUSINESS_CORE,
    label: 'Business workspace',
    description: 'Business financial workspace and team',
    minTier: 'Business',
  },
] as const;

const STATE_CONFIG: Record<
  string,
  { label: string; color: string; bg: string }
> = {
  free: { label: 'Free', color: C.muted, bg: alpha(C.muted, 0.1) },
  trialing: { label: 'Trial', color: C.blue, bg: alpha(C.blue, 0.1) },
  premium: { label: 'Premium', color: C.green, bg: alpha(C.green, 0.1) },
  expired: { label: 'Expired', color: C.red, bg: alpha(C.red, 0.1) },
  cancelled: { label: 'Cancelled', color: C.red, bg: alpha(C.red, 0.1) },
  needs_attention: {
    label: 'Needs attention',
    color: C.orange,
    bg: alpha(C.orange, 0.1),
  },
};

const PLANS = [
  {
    key: 'free',
    label: 'Free',
    price: '£0',
    description: 'Core budgeting tools',
    highlight: false,
  },
  {
    key: 'plus',
    label: 'Plus',
    price: '£5.99/mo',
    description: 'Full personal finance suite',
    highlight: true,
  },
  {
    key: 'family',
    label: 'Family',
    price: '£8.99/mo',
    description: 'Household + shared goals',
    highlight: false,
  },
  {
    key: 'business',
    label: 'Business',
    price: 'Contact',
    description: 'Business workspace + team',
    highlight: false,
  },
];

function fmt(dateStr: string | null): string {
  if (!dateStr) return '–';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const SubscriptionPage = (): JSX.Element => {
  const [info, setInfo] = useState<EntitlementInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getEntitlement()
      .then((r) => setInfo(r.data.entitlement))
      .catch(() => {
        /* silently fail */
      })
      .finally(() => setLoading(false));
  }, []);

  const cfg = info ? (STATE_CONFIG[info.state] ?? STATE_CONFIG['free']) : null;

  return (
    <UserDashboard>
      <Container paddingY={4}>
        <Box mb={3}>
          <Typography variant="h4" fontWeight={700} sx={{ color: C.ink }}>
          Subscription
          </Typography>
          <Typography variant="body2" sx={{ color: C.muted, mt: 0.5 }}>
          Manage your Docket plan
          </Typography>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {!loading && info && cfg && (
          <>
            {/* Current state card */}
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: C.ink }}>
                  Current plan
                  </Typography>
                  <Chip
                    label={cfg.label}
                    size="small"
                    sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700 }}
                  />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: C.muted }}>
                    Plan
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ color: C.ink }}
                    >
                      {info.plan ?? 'Free'}
                    </Typography>
                  </Grid>
                  {info.trialEndDate && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: C.muted }}>
                      Trial ends
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ color: C.orange }}
                      >
                        {fmt(info.trialEndDate)}
                      </Typography>
                    </Grid>
                  )}
                  {info.renewalDate && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: C.muted }}>
                      Renews
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ color: C.ink }}
                      >
                        {fmt(info.renewalDate)}
                      </Typography>
                    </Grid>
                  )}
                  {info.managingPlatform && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: C.muted }}>
                      Billed via
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ color: C.ink }}
                      >
                        {info.managingPlatform}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Plans grid */}
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: C.ink, mb: 2 }}
            >
            Available plans
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {PLANS.map((plan) => (
                <Grid item xs={12} sm={6} md={3} key={plan.key}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      borderColor: plan.highlight ? C.blue : 'divider',
                      borderWidth: plan.highlight ? 2 : 1,
                      position: 'relative',
                      bgcolor:
                      plan.key === info.tier
                        ? alpha(C.blue, 0.04)
                        : 'background.paper',
                    }}
                  >
                    {plan.highlight && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: C.blue,
                          color: '#fff',
                          px: 1.5,
                          py: 0.25,
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                      Most popular
                      </Box>
                    )}
                    <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ color: C.ink }}
                      >
                        {plan.label}
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight={800}
                        sx={{ color: C.blue, my: 1 }}
                      >
                        {plan.price}
                      </Typography>
                      <Typography variant="caption" sx={{ color: C.muted }}>
                        {plan.description}
                      </Typography>
                      {plan.key === info.tier && (
                        <Box mt={1.5}>
                          <Chip
                            label="Current"
                            size="small"
                            sx={{
                              bgcolor: alpha(C.green, 0.1),
                              color: C.green,
                              fontWeight: 700,
                            }}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Feature access breakdown */}
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: C.ink, mb: 2 }}
            >
            Your feature access
            </Typography>
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
              <CardContent sx={{ p: 0 }}>
                {FEATURE_CATALOGUE.map((item, idx) => {
                  const enabled = info.enabledFeatures.includes(item.key);
                  return (
                    <Box key={item.key}>
                      {idx > 0 && <Divider />}
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={2.5}
                        py={1.5}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: enabled ? C.ink : C.muted }}
                          >
                            {item.label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: C.muted }}>
                            {item.description}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flexShrink={0}
                          ml={2}
                        >
                          {enabled ? (
                            <>
                              <CheckCircleOutlinedIcon
                                sx={{ fontSize: 18, color: C.green }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                sx={{ color: C.green }}
                              >
                              Included
                              </Typography>
                            </>
                          ) : (
                            <>
                              <LockOutlinedIcon
                                sx={{ fontSize: 16, color: C.muted }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: C.muted }}
                              >
                                {item.minTier}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>

            {/* iOS CTA */}
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              <Typography variant="body2" fontWeight={600}>
              Subscribe or upgrade via the Docket iOS app
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: C.muted, mt: 0.5, display: 'block' }}
              >
              Subscriptions are managed through the App Store. Open the Docket
              app on your iPhone to change your plan.
              </Typography>
            </Alert>
          </>
        )}

        {!loading && !info && (
          <Alert severity="error" sx={{ borderRadius: 3 }}>
          Unable to load subscription information. Please try again later.
          </Alert>
        )}
      </Container>
    </UserDashboard>
  );
};

export default SubscriptionPage;
